// src/pages/admin/AdminLayout.jsx
import React, { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PhotoIcon,
  QueueListIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  ListBulletIcon,
  CurrencyDollarIcon, // ← new icon import
} from "@heroicons/react/24/outline";
import { useAuth } from "../../context/AuthContext";
import { BiMailSend } from "react-icons/bi";
import { ROLE_PERMISSIONS } from "../../data/roles";
import { FaCarAlt } from "react-icons/fa";
import { TbTransactionEuro } from "react-icons/tb";
import { IoCartOutline } from "react-icons/io5";

const SECTIONS = [
  { key: "vehicles", label: "Vehicles", Icon: FaCarAlt },
  { key: "feature-catalog", label: "Feature Catalog", Icon: ListBulletIcon },
  { key: "images", label: "Images", Icon: PhotoIcon },
  { key: "requests", label: "Requests", Icon: QueueListIcon },
  { key: "users", label: "Users", Icon: UserIcon },
  { key: "email-settings", label: "Email Settings", Icon: BiMailSend },
  { key: "warranty", label: "Warranty Packages", Icon: CurrencyDollarIcon },
  {
    key: "warranty-transactions",
    label: "Warranty Transactions",
    Icon: TbTransactionEuro,
  },
  { key: "orders", label: "Orders Management", Icon: IoCartOutline },
  // { key: "flex-prices", label: "Flex Prices", Icon: CurrencyDollarIcon }, // ← new section
];

export default function AdminLayout() {
  const { user, loading, clearAuth } = useAuth();
  const [collapsed, setCollapsed] = useState(true);
  if (loading) return null;

  const roles = user.roles ?? [];
  const allowed = new Set(roles.flatMap((r) => ROLE_PERMISSIONS[r] || []));

  const title = roles.includes("super-admin")
    ? "Super Admin"
    : roles.includes("car-admin")
    ? "Vehicle Admin"
    : roles.includes("image-admin")
    ? "Image Admin"
    : roles.includes("enquiry-admin")
    ? "Enquiry Admin"
    : "Admin";

  return (
    <div className="flex min-h-screen bg-bg">
      <aside
        className={`
          flex flex-col
          ${collapsed ? "w-16" : "w-64"}
          h-screen sticky top-0
          bg-surface text-on-surface
          transition-[width] duration-200 ease-in-out
          shadow-lg
        `}
      >
        {/* header + toggle */}
        <div className="flex items-center justify-between p-4">
          {!collapsed && <h2 className="text-lg font-bold">{title} Panel</h2>}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="p-1 rounded hover:bg-secondary/20 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {collapsed ? (
              <ChevronRightIcon className="h-6 w-6 text-on-surface" />
            ) : (
              <ChevronLeftIcon className="h-6 w-6 text-on-surface" />
            )}
          </button>
        </div>

        {/* nav links */}
        <nav className="flex-1 px-2 space-y-1 overflow-y-auto hide-scrollbar">
          {SECTIONS.map(({ key, label, Icon }) =>
            allowed.has(key) ? (
              <NavLink
                key={key}
                to={`/admin/${key}`}
                title={collapsed ? label : undefined}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-3 py-2 rounded-md transition-colors duration-150
                   ${
                     isActive
                       ? "bg-primary text-on-surface"
                       : "hover:bg-secondary/30"
                   }`
                }
              >
                <Icon className="h-5 w-5 flex-shrink-0 text-current" />
                {!collapsed && <span>{label}</span>}
              </NavLink>
            ) : null
          )}
        </nav>

        {/* logout */}
        <div className="p-4 border-t border-secondary-accent">
          {collapsed ? (
            <div className="flex justify-center">
              <button
                onClick={clearAuth}
                className="p-2 rounded-md hover:bg-secondary/20 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 text-on-surface" />
              </button>
            </div>
          ) : (
            <button
              onClick={clearAuth}
              className="w-full flex items-center justify-center space-x-2 py-2 bg-accent hover:bg-brandAccent rounded-md transition focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 text-white" />
              <span>Logout</span>
            </button>
          )}
        </div>
      </aside>

      {/* main content */}
      <main className="flex-1 p-6 bg-bg text-on-surface">
        <Outlet />
      </main>
    </div>
  );
}
